import type { EntityName } from "@engram/core";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { booting } from "../boot";
import type { LocalMutation, Request, SyncState, WorkerConfig, WorkerEvent } from "./protocol";

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();
const tableListeners = new Set<(tables: Set<EntityName>) => void>();
let syncState: SyncState = { status: "idle", pending: 0, lastSync: null };
const syncListeners = new Set<() => void>();
const corruptListeners = new Set<(error: string) => void>();

/*
 * The last result of each query, so a page that mounts again paints at once from it instead of empty → filled (which
 * also shifted the layout, e.g. Home's greeting and widgets). An entry is dropped the moment one of its tables
 * changes, and a result that was in flight across a change is never stored, so a cached result is never stale.
 * Note bodies are not kept (big); at most MAX_CACHED queries, least recently used out first.
 */
const MAX_CACHED = 150;
const resultCache = new Map<string, { rows: unknown[]; tables: EntityName[] }>();
let changeGen = 0;
function cacheGet<T>(key: string): T[] | undefined {
  const e = resultCache.get(key);
  if (!e) return undefined;
  resultCache.delete(key); // most recently used goes last
  resultCache.set(key, e);
  return e.rows as T[];
}
/** Queries that can return note bodies (`body_html`, or `SELECT *` from notes) aren't kept. */
const BODIES = /body_html|select\s+(\w+\.)?\*[\s\S]*\bfrom\s+notes\b/i;
function cachePut(key: string, sql: string, rows: unknown[], tables: EntityName[]) {
  if (BODIES.test(sql)) return;
  resultCache.delete(key);
  resultCache.set(key, { rows, tables });
  if (resultCache.size > MAX_CACHED) resultCache.delete(resultCache.keys().next().value!);
}

/** Called when the worker finds the local database damaged (see worker.ts `rebuild`). */
export function onCorrupt(fn: (error: string) => void): () => void {
  corruptListeners.add(fn);
  return () => corruptListeners.delete(fn);
}

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module", name: "engram-db" });
  worker.onmessage = (ev: MessageEvent<{ id?: number; result?: unknown; error?: string } & Partial<WorkerEvent>>) => {
    const msg = ev.data;
    if (msg.event === "changed") {
      const set = new Set(msg.tables);
      changeGen++;
      for (const [k, e] of resultCache) if (e.tables.some((t) => set.has(t))) resultCache.delete(k);
      for (const l of tableListeners) l(set);
      return;
    }
    if (msg.event === "corrupt") {
      for (const l of corruptListeners) l(msg.error!);
      return;
    }
    if (msg.event === "sync") {
      syncState = msg.state!;
      for (const l of syncListeners) l();
      return;
    }
    const p = pending.get(msg.id!);
    if (!p) return;
    pending.delete(msg.id!);
    if (msg.error) p.reject(new Error(msg.error));
    else p.resolve(msg.result);
  };
  return worker;
}

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

/** Set once this tab handed the database to another tab: later calls never start a new worker. */
let closed = false;
/** Saves still waiting in a debounce (an editor, a title), run before the database is handed to another tab. */
const savers = new Set<() => Promise<unknown>>();

/** Registers a pending-save flusher; returns the unregister function. */
export function onBeforeClose(save: () => Promise<unknown>): () => void {
  savers.add(save);
  return () => void savers.delete(save);
}

function call<T>(req: DistributiveOmit<Request, "id">): Promise<T> {
  // The tab reloads into "open in another tab" right after a handover; until then calls just wait (no new worker
  // racing the other tab for the files, no errors from timers still firing).
  if (closed) return new Promise<T>(() => {});
  const w = ensureWorker();
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    w.postMessage({ ...req, id });
  });
}

export const db = {
  init: (cfg: WorkerConfig) => call<SyncState>({ op: "init", cfg }),
  // Reads made while the start screen is up are part of the first screen (boot.ts).
  query: <T = Record<string, unknown>>(sql: string, params: unknown[] = []) => booting(call<T[]>({ op: "query", sql, params })),
  async get<T = Record<string, unknown>>(sql: string, params: unknown[] = []): Promise<T | undefined> {
    return (await db.query<T>(sql, params))[0];
  },
  mutate: (muts: LocalMutation[]) => call<true>({ op: "mutate", muts }).then(() => scheduleSync()),
  sync: () => call<SyncState>({ op: "sync" }),
  addFile: (sha: string, blob: Blob) => call<true>({ op: "addFile", sha, blob }),
  reset: () => call<true>({ op: "reset" }),
  /** Wipes this device's replica and opens an empty one (the next sync refills it). */
  rebuild: (cfg: WorkerConfig) => call<SyncState>({ op: "rebuild", cfg }),
  /** Runs every pending save (at most `ms`), so typing from just before a handover reaches the local database. */
  async saveAll(ms = 2000): Promise<void> {
    await Promise.race([Promise.allSettled([...savers].map((s) => s())), new Promise((r) => setTimeout(r, ms))]);
  },
  get closed() {
    return closed;
  },
  /** Stops the worker so its OPFS handles are released (another tab is taking over). */
  close() {
    closed = true;
    worker?.terminate();
    worker = null;
    pending.clear(); // left unsettled, like calls made from now on (see call())
  },
};

// Push shortly after edits settle: 1 s after the last local save (the editor saves ~0.4 s after the last key), so
// another device sees a change ~1.5 s after typing stops. Each push is one request and a few D1 rows written.
let syncTimer: ReturnType<typeof setTimeout> | undefined;
export function scheduleSync(delay = 1000) {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => void db.sync(), delay);
}

export function onTablesChanged(fn: (tables: Set<EntityName>) => void): () => void {
  tableListeners.add(fn);
  return () => tableListeners.delete(fn);
}

export function getSyncState(): SyncState {
  return syncState;
}

export function onSyncState(fn: (s: SyncState) => void): () => void {
  const l = () => fn(syncState);
  syncListeners.add(l);
  return () => void syncListeners.delete(l);
}

export function useSyncState(): SyncState {
  return useSyncExternalStore(
    (cb) => {
      syncListeners.add(cb);
      return () => syncListeners.delete(cb);
    },
    () => syncState,
  );
}

/**
 * Runs a query and re-runs it whenever one of `tables` changes locally or via sync.
 * Returns undefined until the first result arrives (a query run before paints at once from `resultCache`).
 */
export function useQuery<T = Record<string, unknown>>(sql: string | null, params: unknown[], tables: EntityName[]): T[] | undefined {
  return useQueryState<T>(sql, params, tables).rows;
}

/**
 * Like useQuery, but also says whether `rows` belong to the current sql/params.
 * While a new query runs, the previous rows stay on screen (no flicker) with fresh = false.
 */
export function useQueryState<T = Record<string, unknown>>(sql: string | null, params: unknown[], tables: EntityName[]): { rows: T[] | undefined; fresh: boolean } {
  const key = JSON.stringify([sql, params]);
  const [state, setState] = useState<{ key: string; rows: T[] } | undefined>(() => {
    const rows = sql == null ? undefined : cacheGet<T>(key);
    return rows ? { key, rows } : undefined;
  });
  const seq = useRef(0);
  useEffect(() => {
    if (sql == null) {
      setState(undefined);
      return;
    }
    let alive = true;
    // At most one run in flight; changes that land meanwhile ask for one more run after it (a burst of changes, e.g.
    // a save then its push, no longer queues a run per change in the worker ahead of what the user does next).
    let busy = false;
    let again = false;
    const load = () => {
      if (busy) {
        again = true;
        return;
      }
      busy = true;
      const n = ++seq.current;
      const gen = changeGen;
      db.query<T>(sql, params)
        .then(
          (r) => {
            if (gen === changeGen) cachePut(key, sql, r, tables);
            // Same rows as on screen: keep the old array, so nothing re-renders.
            if (alive && n === seq.current) setState((prev) => (prev?.key === key && sameRows(prev.rows, r) ? prev : { key, rows: r }));
          },
          (e) => console.error(e, sql),
        )
        .finally(() => {
          busy = false;
          if (again && alive) {
            again = false;
            load();
          }
        });
    };
    load();
    const off = onTablesChanged((changed) => {
      if (tables.some((t) => changed.has(t))) load();
    });
    return () => {
      alive = false;
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tables.join()]);
  if (sql == null) return { rows: undefined, fresh: false };
  if (state?.key === key) return { rows: state.rows, fresh: true };
  // New sql/params: show its cached result at once if there is one, else keep the previous rows (no flicker).
  const cached = cacheGet<T>(key);
  return cached ? { rows: cached, fresh: true } : { rows: state?.rows, fresh: false };
}

/** Row-by-row, column-by-column equality of two query results. */
function sameRows(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x === y) continue;
    if (!x || !y || typeof x !== "object" || typeof y !== "object") return false;
    const rx = x as Record<string, unknown>;
    const ry = y as Record<string, unknown>;
    for (const k in rx) if (rx[k] !== ry[k]) return false;
    for (const k in ry) if (!(k in rx)) return false;
  }
  return true;
}

export function useRow<T = Record<string, unknown>>(sql: string | null, params: unknown[], tables: EntityName[]): T | undefined | null {
  const rows = useQuery<T>(sql, params, tables);
  if (sql == null) return null;
  return rows === undefined ? undefined : (rows[0] ?? null);
}

// Dev builds only: lets a console or test script run queries against the replica (e.g. EXPLAIN QUERY PLAN).
// Dev builds, and test builds of the Android app (VITE_TEST_HOOKS=1, apps/android/test-updates.mjs).
if (import.meta.env.DEV || import.meta.env.VITE_TEST_HOOKS) (globalThis as { __engramDb?: typeof db }).__engramDb = db;
